import React, { useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Link as RouterLink } from "react-router-dom";

const theme = createTheme();

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 로그인 상태 확인
  useEffect(()=>{
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null);
    });

    return () => authListener.subscription.unsubscribe();
  })

  // 📌 일반 이메일 로그인 처리
  const handleSubmit = async (event) => {
    event.preventDefault();

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      showSnackbar("로그인 실패: " + error.message, "warning");
    } else {
      showSnackbar("로그인 성공!", "success");
      navigate("/");
    }
  };

  // 1) 환경 감지 함수
  const isInAppWebView = () => {
    return (
      typeof window.Android === "object" ||
      !!(window.webkit && window.webkit.messageHandlers?.signInWithGoogle)
    );
  };

  // 📌 Google 로그인 처리
  const handleGoogleLogin = async ()=>{
    var is_app = isInAppWebView();
    console.log("IS APPLICATION:"+is_app);

    if (is_app) {
      window.onGoogleSignInResult = async (idToken) => {
        if (!idToken) {
          showSnackbar("앱 구글 로그인 실패", "warning");
          return;
        }
        
        console.log(idToken);
        // // Supabase에 토큰 전달해서 세션 발급
        // Supabase에 ID 토큰 전달해서 세션 발급
        const res = await supabase.auth.signInWithIdToken({
          provider: "google",  // ← OIDC 공급자 Name 과 완전히 일치시켜야 합니다
          token:      idToken,
        });
        
        const { data, error } = res;
        console.log(data);

        if (error){
          showSnackbar("세션 생성 실패: " + error.message, "warning");
        }else{
          console.log(data);
          navigate("/auth/callback");
        }
      };

      // 네이티브 호출 (WebAppInterface.signInWithGoogle)
      window.Android?.signInWithGoogle("onGoogleSignInResult");
      window.webkit?.messageHandlers?.signInWithGoogle?.postMessage("onGoogleSignInResult");
    }else{
      const {error} = await supabase.auth.signInWithOAuth({
        provider:"google",
        options : {
          redirectTo : `${window.location.origin}/auth/callback`, // 현재 도메인 기준 리디렉션
        }
      });
      
      if(error){
        alert("Google Login Failed"+error.message);
      }
    }
  };
  
  return (
    <ThemeProvider theme={theme}>
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            backgroundImage: "url(https://source.unsplash.com/random)",
            backgroundRepeat: "no-repeat",
            backgroundColor: (t) =>
              t.palette.mode === "light" ? t.palette.grey[50] : t.palette.grey[900],
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <Box
            sx={{
              my: 8,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5" sx={{ fontFamily: "SeoulHangang" }}>
            {user ? "로그인 상태" : "로그인"}
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="이메일 주소"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="비밀번호"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <FormControlLabel control={<Checkbox value="remember" color="primary" />} label="로그인 유지" />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
                로그인
              </Button>
              <Button fullWidth variant="outlined" sx={{mt:1, mb:2}} onClick={handleGoogleLogin}>
                Google 계정으로 로그인
              </Button>
              <Grid container>
                <Grid item xs>
                  <Link href="#" variant="body2">
                    비밀번호 찾기
                  </Link>
                </Grid>
                <Grid item>
                  <Link component={RouterLink} to="/signup" variant="body2">
                    {"회원가입"}
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* 🎉 Snackbar 알림 영역 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
